/**
 * The "Tasks" section of a record slideout — the tasks linked to this
 * contact / company / deal / meeting. A task is a case, so it reaches the
 * record through the case-kind rules every case has (`ahoy_deal_to_contact`,
 * …); this section lists `ahoy_task` objects by parent record and does not
 * care which rule carried the link. (The overview's inherited row still wears
 * the deal-flavoured label until #12411 gives labels per entity-type pair.)
 *
 * Shown when the record's entity type carries a live ahoy_task association
 * (RecordViewContext's `tasksEnabled`, derived purely from the org's schema —
 * `hasTaskAssociation` and the resolved sections config). That render gate is
 * broad: it includes hosts reachable only through an internal transitive rule
 * (a meeting via contact — AC-10), so a meeting still lists its derived tasks.
 * The "Add task" affordance uses the STRICT `useHasWritableTaskAssociation`
 * gate instead — the composer can only write a user-writable host link.
 *
 * Deliberately the notes section's twin: the same NoteCard, the same
 * useMemoRecords-backed edit / delete / stage-change wiring, run under the task
 * family (`useMemoRecords({ recordType: 'ahoy_task' })`). The pencil opens the
 * shared compose panel on the task, exactly as it does for a note.
 */

import { Plus } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useMemoCompose } from '@/contexts/MemoComposeContext';
import { useCurrentUserEntityId, useOrgMembers } from '@/hooks/useOrgMembers';
import { usePermissions } from '@/hooks/usePermissions';
import { QueryErrorState } from '@/lib/components';

import { getMemoCreatorName, getMemoOwnerName } from '../notes/memoMembers';
import { NoteCard } from '../notes/NoteCard';
import { type MemoPrivacy, memoPrivacy } from '../notes/notes.utils';
import {
  type Note,
  useMemoPrivacyToggle,
  useMemoRecords,
} from '../notes/useMemoRecords';
import { useHasWritableTaskAssociation } from '../task/useHasTaskAssociation';
import { useTaskTabActions } from '../task/useTaskTabActions';
import { CollapsibleSection } from './collapsible-section';
import { SectionActionButton } from './section-action-button';

interface TasksSectionProps {
  orgId: number;
  entityId: string;
  entityTypeName: string;
}

export function TasksSection({
  orgId,
  entityId,
  entityTypeName,
}: TasksSectionProps) {
  const { hasPermission } = usePermissions();
  const canDeleteTask = hasPermission('ahoy_task', 'ahoy_object_deleted');
  // The stage pill saves via an ahoy_association_added event — gate on the
  // event the mutation actually submits (see NotesSection's canChangeStage).
  const canChangeStage = hasPermission('ahoy_task', 'ahoy_association_added');

  const { user } = useAuthInfo();
  const userId = user?.id ? String(user.id) : undefined;
  const { members: orgUsers } = useOrgMembers(orgId);
  const userEntityId = useCurrentUserEntityId(orgId);

  const {
    records: tasks,
    isLoading,
    error: fetchError,
    refetch,
    updateRecordAsync,
    updateRecordStageAsync,
    isUpdating,
    updateError,
    deleteRecord,
    isDeleting,
    deleteError,
  } = useMemoRecords({
    orgId,
    entityId,
    entityTypeName,
    users: orgUsers,
    recordType: 'ahoy_task',
  });
  const { togglePrivacy } = useMemoPrivacyToggle({
    orgId,
    recordType: 'ahoy_task',
    users: orgUsers,
  });

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // "Add task" opens the shared composer pre-linked to this record with the
  // default task stage selected — the same hook the record's Tasks tab uses,
  // so both entry points open identically.
  const { startCreatingTask, canCreateTask, isStageLoading } =
    useTaskTabActions(entityId, entityTypeName);
  // The section RENDERS for any host with a live task association (including a
  // meeting reachable only via an internal transitive rule — AC-10), but the
  // composer can only write a USER-WRITABLE host link. Gate "Add task" on the
  // strict predicate so a meeting lists its derived tasks without offering a
  // create the schema would drop on submit.
  const hasWritableTaskAssociation = useHasWritableTaskAssociation(
    orgId,
    entityTypeName
  );
  const { editMemo } = useMemoCompose();

  // The pencil opens the compose panel on the task (the panel resolves the
  // record's family from the object itself).
  const handleEditTaskClick = useCallback(
    (task: Note) => {
      editMemo(task.entity_id);
    },
    [editMemo]
  );

  // Track which task an inline edit / delete targets so the shared mutation
  // state only surfaces on that card — mirrors NotesSection.
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);

  const handleEditTask = useCallback(
    (
      taskEntityId: string,
      title: string,
      content: string,
      opts?: {
        date?: string;
        ownerEntityId?: string;
        stageAssociation?: string | null;
        privacy?: MemoPrivacy;
      }
    ) => {
      const { date, ownerEntityId, stageAssociation, privacy } = opts ?? {};
      const task = tasks.find(t => t.entity_id === taskEntityId);
      if (!task || !userId) {
        return;
      }
      setEditingTaskId(task.entity_id);
      // Sequence the edit before the privacy toggle: both advance the task's
      // seq, so running them concurrently can reject one with a stale seq.
      const runEdit = async () => {
        try {
          await updateRecordAsync({
            noteEntityId: task.entity_id,
            title,
            content,
            date,
            ownerEntityId,
            originalTitle: task.title,
            originalContent: task.content,
            originalDate: task.date,
            originalDatetime: task.datetime,
            originalOwnerEntityId: task.owner_entity_id,
            stageAssociation: stageAssociation ?? null,
            originalStageAssociation: task.stage_entity_id ?? null,
            lastEventSeq: task.last_event_seq,
            userId,
          });
        } catch {
          // Keep `editingTaskId` set so `editError` stays visible in the
          // card's edit form; skip the privacy toggle so a failed edit never
          // applies a partial change.
          return;
        }

        if (privacy && privacy !== memoPrivacy(task)) {
          const visibilitySaved = await togglePrivacy(task.entity_id, privacy);
          if (!visibilitySaved) {
            // Keep `editingTaskId` set so the edit stays open and retryable —
            // togglePrivacy already toasted + logged the rejection. Closing here
            // would read to the user as a successful save.
            return;
          }
        }

        setEditingTaskId(null);
      };
      void runEdit();
    },
    [updateRecordAsync, togglePrivacy, userId, tasks]
  );

  const handleDeleteTask = useCallback(
    async (taskEntityId: string) => {
      const task = tasks.find(t => t.entity_id === taskEntityId);
      if (!task || !userId) {
        return;
      }
      // Not caught on purpose: NoteCard's confirm dialog needs the rejection
      // to stay open and render `deleteError` (see NotesSection).
      setDeletingTaskId(taskEntityId);
      await deleteRecord({
        noteEntityId: taskEntityId,
        lastEventSeq: task.last_event_seq ?? 0,
        userId,
      });
    },
    [deleteRecord, tasks, userId]
  );

  // Throwing (rather than resolving) on a missing user matters: the picker
  // clears its optimistic stage in the promise's `.catch`, so a silent resolve
  // would leave the pill showing an unsaved stage with its trigger disabled.
  const handleStageChange = useCallback(
    async (taskEntityId: string, newStageEntityId: string | null) => {
      const task = tasks.find(t => t.entity_id === taskEntityId);
      if (!task || !userId) {
        throw new Error('Cannot change task stage: task or user unavailable');
      }
      await updateRecordStageAsync(task, newStageEntityId, userId);
    },
    [updateRecordStageAsync, tasks, userId]
  );
  const stageChangeHandler =
    canChangeStage && userId ? handleStageChange : undefined;

  const actionButtons = useMemo(
    () =>
      canCreateTask && userEntityId && hasWritableTaskAssociation ? (
        <SectionActionButton
          label='Add task'
          onClick={startCreatingTask}
          icon={Plus}
          disabled={isStageLoading}
          disabledTooltip='Loading…'
        />
      ) : undefined,
    [
      canCreateTask,
      userEntityId,
      hasWritableTaskAssociation,
      startCreatingTask,
      isStageLoading,
    ]
  );

  return (
    <CollapsibleSection title='Tasks' actions={actionButtons}>
      {isLoading ? (
        <div
          className='h-16 rounded-lg bg-[var(--color-bg-tertiary)] animate-pulse'
          aria-hidden='true'
        />
      ) : (
        <div className='space-y-4'>
          {fetchError && (
            <QueryErrorState
              error={fetchError}
              label="We couldn't load tasks"
              onRetry={handleRetry}
              size='sm'
            />
          )}

          {!fetchError && tasks.length === 0 && (
            <p className='text-xs md:text-sm text-fg-secondary'>No tasks yet</p>
          )}

          {!fetchError && tasks.length > 0 && (
            <div className='space-y-4'>
              {tasks.map((task: Note) => (
                <div
                  key={task.entity_id}
                  data-focused-association-id={task.entity_id}
                >
                  <NoteCard
                    note={task}
                    orgId={orgId}
                    entityType='ahoy_task'
                    ownerName={getMemoOwnerName(task, orgUsers)}
                    creatorName={getMemoCreatorName(task, orgUsers)}
                    users={orgUsers}
                    currentUserEntityId={userEntityId}
                    canDelete={canDeleteTask}
                    onEdit={handleEditTask}
                    onEditClick={handleEditTaskClick}
                    onDelete={handleDeleteTask}
                    onStageChange={stageChangeHandler}
                    isEditing={isUpdating && editingTaskId === task.entity_id}
                    isDeleting={isDeleting && deletingTaskId === task.entity_id}
                    editError={
                      editingTaskId === task.entity_id ? updateError : null
                    }
                    deleteError={
                      deletingTaskId === task.entity_id &&
                      deleteError instanceof Error
                        ? deleteError
                        : null
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}
